import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import { ApiCall } from "../helper/axios";
// ==========================================================================
// ASYNC THUNKS
// ==========================================================================
export const fetchOutreachPerformance = createAsyncThunk("outreachPerformance/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("GET", "/api/outreach-performance", payload, {
            Authorization: token,
            "x-module-key": "outreach_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return responseData.data;
        }
        return rejectWithValue(responseData?.message || "Failed to fetch outreach performance");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching outreach performance");
    }
});
export const createPublishedArticle = createAsyncThunk("outreachPerformance/createArticle", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/save-published-article", payload, {
            Authorization: token,
            "x-module-key": "outreach_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return responseData.data;
        }
        return rejectWithValue(responseData?.message || "Failed to save published article");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while saving the published article");
    }
});
export const updatePublishedArticle = createAsyncThunk("outreachPerformance/updateArticle", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/update-published-article", payload, {
            Authorization: token,
            "x-module-key": "outreach_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return responseData.data;
        }
        return rejectWithValue(responseData?.message || "Failed to update published article");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while updating the published article");
    }
});
export const deletePublishedArticle = createAsyncThunk("outreachPerformance/deleteArticle", async ({ id }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/delete-published-article", { id }, {
            Authorization: token,
            "x-module-key": "outreach_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return responseData.data;
        }
        return rejectWithValue(responseData?.message || "Failed to delete published article");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while deleting the published article");
    }
});
export const syncOutreachLeads = createAsyncThunk("outreachPerformance/syncLeads", async ({ startDate, endDate }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("POST", "/api/sync-outreach-leads", { start_date: startDate, end_date: endDate }, {
            Authorization: token,
            "x-module-key": "outreach_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return {
                ...responseData.data,
                message: responseData.message,
            };
        }
        return rejectWithValue(responseData?.message || "Failed to sync outreach leads from HubSpot");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while syncing outreach leads");
    }
});
export const exportOutreachPerformanceReport = createAsyncThunk("outreachPerformance/export", async (payload, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("GET", "/api/export-outreach-performance", payload, {
            Authorization: token,
            "x-module-key": "outreach_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return responseData.data;
        }
        return rejectWithValue(responseData?.message || "Failed to export outreach performance");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while exporting outreach performance");
    }
});
export const fetchPublishedArticles = createAsyncThunk("outreachPerformance/fetchArticles", async (params, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth?.token || "";
        const response = await ApiCall("GET", "/api/published-articles", params, {
            Authorization: token,
            "x-module-key": "outreach_performance",
        });
        const responseData = response?.data;
        if (responseData?.success) {
            return responseData.data;
        }
        return rejectWithValue(responseData?.message || "Failed to fetch published articles");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching published articles");
    }
});
// ==========================================================================
// INITIAL STATE
// ==========================================================================
const getDefaultDateRange = () => {
    const today = moment();
    const isFirstDayOfMonth = today.date() === 1;
    // If today is the 1st of the month (e.g. Sep 1), default to Last Month (e.g. Aug 1 - Aug 31)
    // Otherwise, default to This Month MTD-1 (e.g. Sep 1 - Sep 4)
    const start = isFirstDayOfMonth
        ? today.clone().subtract(1, "month").startOf("month")
        : today.clone().startOf("month");
    const end = today.clone().subtract(1, "day");
    // Default comparison: same days into previous month (MoM)
    const compareStart = start.clone().subtract(1, "month");
    const compareEnd = end.clone().subtract(1, "month");
    return {
        startDate: start.format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
        compareStartDate: compareStart.format("YYYY-MM-DD"),
        compareEndDate: compareEnd.format("YYYY-MM-DD"),
    };
};
const getInitialState = () => {
    let saved = null;
    try {
        const raw = localStorage.getItem("outreach_perf_filters");
        if (raw)
            saved = JSON.parse(raw);
    }
    catch { }
    const defaults = getDefaultDateRange();
    return {
        entities: [],
        teamTotal: null,
        byClient: null,
        bySpecialist: null,
        specialists: [],
        cache: null,
        loading: false,
        error: null,
        groupBy: (saved?.groupBy === "client" || saved?.groupBy === "specialist") ? saved.groupBy : "client",
        startDate: saved?.startDate || defaults.startDate,
        endDate: saved?.endDate || defaults.endDate,
        compareStartDate: saved?.compareStartDate ?? defaults.compareStartDate,
        compareEndDate: saved?.compareEndDate ?? defaults.compareEndDate,
        savingArticle: false,
        saveArticleError: null,
        lastUpdated: null,
    };
};
const initialState = getInitialState();
// ==========================================================================
// SLICE
// ==========================================================================
const outreachPerformanceSlice = createSlice({
    name: "outreachPerformance",
    initialState,
    reducers: {
        setGroupBy: (state, action) => {
            state.groupBy = action.payload;
            try {
                const current = JSON.parse(localStorage.getItem("outreach_perf_filters") || "{}");
                localStorage.setItem("outreach_perf_filters", JSON.stringify({ ...current, groupBy: action.payload }));
            }
            catch { }
            if (action.payload === "specialist" && state.bySpecialist) {
                state.entities = state.bySpecialist.entities;
                state.teamTotal = state.bySpecialist.teamTotal;
            }
            else if (action.payload === "client" && state.byClient) {
                state.entities = state.byClient.entities;
                state.teamTotal = state.byClient.teamTotal;
            }
        },
        setDateRange: (state, action) => {
            state.startDate = action.payload.startDate;
            state.endDate = action.payload.endDate;
            try {
                const current = JSON.parse(localStorage.getItem("outreach_perf_filters") || "{}");
                localStorage.setItem("outreach_perf_filters", JSON.stringify({ ...current, startDate: action.payload.startDate, endDate: action.payload.endDate }));
            }
            catch { }
        },
        setCompareDateRange: (state, action) => {
            state.compareStartDate = action.payload.compareStartDate;
            state.compareEndDate = action.payload.compareEndDate;
            try {
                const current = JSON.parse(localStorage.getItem("outreach_perf_filters") || "{}");
                localStorage.setItem("outreach_perf_filters", JSON.stringify({ ...current, compareStartDate: action.payload.compareStartDate, compareEndDate: action.payload.compareEndDate }));
            }
            catch { }
        },
        clearOutreachPerformance: (state) => {
            state.entities = [];
            state.teamTotal = null;
            state.byClient = null;
            state.bySpecialist = null;
            state.specialists = [];
            state.error = null;
        },
        clearSaveArticleError: (state) => {
            state.saveArticleError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOutreachPerformance.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchOutreachPerformance.fulfilled, (state, action) => {
            state.loading = false;
            if (action.payload.last_updated) {
                state.lastUpdated = action.payload.last_updated;
            }
            if (action.payload.specialists) {
                state.specialists = action.payload.specialists;
            }
            if (action.payload.by_client) {
                state.byClient = {
                    entities: action.payload.by_client.outreach_performance || [],
                    teamTotal: action.payload.by_client.team_total || null,
                };
            }
            if (action.payload.by_specialist) {
                state.bySpecialist = {
                    entities: action.payload.by_specialist.outreach_performance || [],
                    teamTotal: action.payload.by_specialist.team_total || null,
                };
            }
            if (state.groupBy === "specialist" && state.bySpecialist) {
                state.entities = state.bySpecialist.entities;
                state.teamTotal = state.bySpecialist.teamTotal;
            }
            else if (state.groupBy === "client" && state.byClient) {
                state.entities = state.byClient.entities;
                state.teamTotal = state.byClient.teamTotal;
            }
            else {
                state.entities = action.payload.outreach_performance || [];
                state.teamTotal = action.payload.team_total || null;
            }
            state.cache = action.payload.cache ?? null;
            state.error = null;
        })
            .addCase(fetchOutreachPerformance.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch outreach performance";
        })
            .addCase(createPublishedArticle.pending, (state) => {
            state.savingArticle = true;
            state.saveArticleError = null;
        })
            .addCase(createPublishedArticle.fulfilled, (state) => {
            state.savingArticle = false;
            state.saveArticleError = null;
        })
            .addCase(createPublishedArticle.rejected, (state, action) => {
            state.savingArticle = false;
            state.saveArticleError = action.payload || "Failed to save published article";
        })
            .addCase(updatePublishedArticle.pending, (state) => {
            state.savingArticle = true;
            state.saveArticleError = null;
        })
            .addCase(updatePublishedArticle.fulfilled, (state) => {
            state.savingArticle = false;
            state.saveArticleError = null;
        })
            .addCase(updatePublishedArticle.rejected, (state, action) => {
            state.savingArticle = false;
            state.saveArticleError = action.payload || "Failed to update published article";
        });
    },
});
// ==========================================================================
// EXPORTS
// ==========================================================================
export const { setGroupBy, setDateRange, setCompareDateRange, clearOutreachPerformance, clearSaveArticleError, } = outreachPerformanceSlice.actions;
// Selectors
export const selectOutreachPerformanceEntities = (state) => state.outreachPerformance.entities;
export const selectOutreachPerformanceTeamTotal = (state) => state.outreachPerformance.teamTotal;
export const selectOutreachPerformanceByClient = (state) => state.outreachPerformance.byClient;
export const selectOutreachPerformanceBySpecialist = (state) => state.outreachPerformance.bySpecialist;
export const selectOutreachPerformanceSpecialists = (state) => state.outreachPerformance.specialists || [];
export const selectOutreachPerformanceCache = (state) => state.outreachPerformance.cache;
export const selectOutreachPerformanceLoading = (state) => state.outreachPerformance.loading;
export const selectOutreachPerformanceError = (state) => state.outreachPerformance.error;
export const selectOutreachPerformanceGroupBy = (state) => state.outreachPerformance.groupBy;
export const selectOutreachPerformanceLastUpdated = (state) => state.outreachPerformance.lastUpdated;
export const selectSavingArticle = (state) => state.outreachPerformance.savingArticle;
export const selectSaveArticleError = (state) => state.outreachPerformance.saveArticleError;
export default outreachPerformanceSlice.reducer;
